import { useMemo } from "react";
import { Box, Collapse, HStack, useDisclosure, VStack } from "@chakra-ui/react";
import CDS from "design-system";
import EmojiEmptyState from "components/EmojiEmptyState";
import { Task } from "features/Resources/Task/models";
import { isTaskClosed, isTaskOpen } from "features/Resources/Task/helpers";
import { computeTaskStats, getTaskHealthColor } from "features/Resources/Task/helpers/stats";
import TaskCard from "./TaskCard";

type StageStatus = "complete" | "current" | "upcoming";

interface StageTaskGroupRowProps {
  stageKey: string;
  stageLabel: string;
  stageStatus: StageStatus;
  tasks: Task[];
  hideCompleted?: boolean;
  defaultIsOpen?: boolean;
  onTaskClick?: (task: Task) => void;
}

function StageIndicator({ status }: { status: StageStatus }) {
  if (status === "complete") {
    return (
      <Box
        w="20px"
        h="20px"
        borderRadius="full"
        bg="cdsGreen.500"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          as="svg"
          viewBox="0 0 10 10"
          width="11px"
          height="11px"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="1,5 4,8 9,2" />
        </Box>
      </Box>
    );
  }

  if (status === "current") {
    return (
      <Box
        w="20px"
        h="20px"
        borderRadius="full"
        border="2px solid"
        borderColor="brand.500"
        bg="white"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box w="8px" h="8px" borderRadius="full" bg="brand.500" />
      </Box>
    );
  }

  return <Box w="20px" h="20px" borderRadius="full" bg="gray.200" flexShrink={0} />;
}

export default function StageTaskGroupRow({
  stageKey,
  stageLabel,
  stageStatus,
  tasks,
  hideCompleted = false,
  defaultIsOpen = false,
  onTaskClick,
}: StageTaskGroupRowProps) {
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure({ defaultIsOpen });

  const visibleTasks = useMemo(
    () => tasks.filter((t) => t.status !== "upcoming"),
    [tasks],
  );

  const taskStats = useMemo(() => computeTaskStats(visibleTasks), [visibleTasks]);
  const { openCount, urgentCount, overdueCount } = taskStats;
  const healthColor = getTaskHealthColor(visibleTasks);

  const colorMap: Record<string, string> = { red: "red.300", orange: "orange.300", green: "green.300" };
  const barBg = stageStatus === "upcoming" ? "gray.200" : colorMap[healthColor] ?? "gray.200";

  const sortedTasks = useMemo(() => {
    const sortByDueDate = (a: Task, b: Task) => {
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
    };
    const open = visibleTasks.filter(isTaskOpen).sort(sortByDueDate);
    if (hideCompleted) return open;
    const closed = visibleTasks.filter(isTaskClosed).sort(sortByDueDate);
    return [...open, ...closed];
  }, [visibleTasks, hideCompleted]);

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <HStack
        width="100%"
        px={4}
        py={3}
        justify="space-between"
        borderBottomWidth={isExpanded ? "1px" : 0}
        borderColor="gray.200"
        cursor="pointer"
        onClick={toggleExpanded}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggleExpanded(); }}
        _hover={{ bg: "gray.50" }}
        transition="background 0.15s"
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${stageLabel} tasks`}
      >
        <HStack spacing={3}>
          <Box w="5px" h="28px" borderRadius="2px" bg={barBg} flexShrink={0} />
          <StageIndicator status={stageStatus} />
          <CDS.Text12 fontWeight="600" color="content.primary">
            {stageLabel}
          </CDS.Text12>
        </HStack>

        <HStack spacing={2} onClick={(e) => e.stopPropagation()}>
          {openCount > 0 && stageStatus !== "complete" && (
            <CDS.Text10 color="content.tertiary" whiteSpace="nowrap">{openCount} open</CDS.Text10>
          )}
          <CDS.TaskStatusTag.UrgentCountBadge count={urgentCount} size="sm" />
          <CDS.TaskStatusTag.OverdueCountBadge count={overdueCount} size="sm" />
          <Box
            as="button"
            fontSize="sm"
            color="content.tertiary"
            px={2}
            py={1}
            borderRadius="md"
            _hover={{ bg: "gray.100", color: "content.primary" }}
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); toggleExpanded(); }}
            whiteSpace="nowrap"
          >
            {isExpanded ? "Hide" : "Show"}
          </Box>
        </HStack>
      </HStack>

      <Collapse in={isExpanded} animateOpacity>
        <VStack spacing={1} p={3} align="stretch" bg="gray.50">
          {sortedTasks.length > 0 ? (
            sortedTasks.map((task) => (
              <TaskCard key={task.id} task={task} compact onClick={onTaskClick} showAssignee={false} />
            ))
          ) : (
            <EmojiEmptyState compact bg="white" emoji="✨" line1="No tasks for this stage" line2="Nothing assigned here" />
          )}
        </VStack>
      </Collapse>
    </Box>
  );
}
